import { RefObject, useLayoutEffect, useState } from "react";

interface UseSummaryOverflowArgs {
  containerRef: RefObject<HTMLElement | null>;
  measureRef: RefObject<HTMLElement | null>;
  itemCount: number;
  itemsKey: string;
  gapPx?: number;
  minVisibleCount?: number;
}

export const useSummaryOverflow = ({
  containerRef,
  measureRef,
  itemCount,
  itemsKey,
  gapPx,
  minVisibleCount = 0,
}: UseSummaryOverflowArgs): number => {
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measureRow = measureRef.current;
    if (!container || !measureRow) {
      setVisibleCount(itemCount);
      return;
    }

    const computeVisibleCount = () => {
      const available = container.clientWidth;
      const children = Array.from(measureRow.children) as HTMLElement[];
      if (children.length < itemCount + 1 || available <= 0) {
        setVisibleCount(itemCount);
        return;
      }

      const itemWidths = children.slice(0, itemCount).map((child) => child.offsetWidth);
      const counterWidth = children[itemCount].offsetWidth;
      const itemGap = gapPx ?? (Number.parseFloat(getComputedStyle(measureRow).columnGap) || 0);

      const fullWidth = itemWidths.reduce((total, width) => total + width, 0) + itemGap * (itemCount - 1);
      if (fullWidth <= available) {
        setVisibleCount(itemCount);
        return;
      }

      let fitted = 0;
      let used = counterWidth;
      for (let index = 0; index < itemCount; index += 1) {
        const nextUsed = used + itemWidths[index] + itemGap;
        if (nextUsed > available) {
          break;
        }
        used = nextUsed;
        fitted = index + 1;
      }
      setVisibleCount(Math.max(fitted, minVisibleCount));
    };

    computeVisibleCount();

    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(computeVisibleCount);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, measureRef, itemCount, itemsKey, gapPx, minVisibleCount]);

  return Math.min(visibleCount, itemCount);
};
